// ═══════════════════════════════════════════════════════════════════════
//  DIRECTIVE: t (i18n translations)
//  DIRECTIVE: i18n-ns (load namespace before children)
// ═══════════════════════════════════════════════════════════════════════

import { _i18n, _loadI18nNamespace, _notifyI18n } from "../i18n.js";
import { _watchExpr, _watchI18n } from "../globals.js";
import { evaluate } from "../evaluate.js";
import { findContext, _sanitizeHtml } from "../dom.js";
import { registerDirective, processTree, _disposeChildren } from "../registry.js";

registerDirective("t", {
  priority: 20,
  gated: true,
  init(el, name, key) {
    const ctx = findContext(el);
    const useHtml = el.hasAttribute("t-html");

    // Collect t-* param expressions for the sniff string so $store/$route
    // references in translation params register watchers (NOJS-248).
    const paramExprs = [];
    for (const attr of [...el.attributes]) {
      if (attr.name.startsWith("t-") && attr.name !== "t" && attr.name !== "t-html") {
        paramExprs.push(attr.value);
      }
    }
    const sniff = paramExprs.length ? key + " " + paramExprs.join(" ") : key;

    function update() {
      const params = {};
      for (const attr of [...el.attributes]) {
        if (attr.name.startsWith("t-") && attr.name !== "t" && attr.name !== "t-html") {
          const paramName = attr.name.replace("t-", "");
          params[paramName] = evaluate(attr.value, ctx) ?? attr.value;
        }
      }
      const text = _i18n.t(key, params);
      if (useHtml) {
        _disposeChildren(el);
        el.innerHTML = _sanitizeHtml(text);
      } else {
        el.textContent = text;
      }
    }

    _watchExpr(sniff, ctx, update);
    _watchI18n(update);
    update();
  },
});

registerDirective("i18n-ns", {
  priority: 1,
  init(el, name, ns) {
    // Empty ns = marker attribute (e.g. route-view); skip loading
    if (!ns) return;

    // Save cloned children before detaching so that if (priority 10) can
    // snapshot the original content even though i18n-ns runs first (NOJS-258).
    el.__i18nSavedChildren = [...el.childNodes].map((n) => n.cloneNode(true));

    // Save children to prevent premature t resolution
    const saved = document.createDocumentFragment();
    while (el.firstChild) saved.appendChild(el.firstChild);

    _loadI18nNamespace(ns).then(() => {
      // When `if` co-exists on this element (__ifState exposed by B1),
      // let `if` manage children lifecycle via its snapshot.
      // - false: children were cleared; if's snapshot restores on flip
      // - true: if already restored from snapshot; don't duplicate
      // The namespace is loaded; notify so active t directives re-render.
      if (el.__ifState !== undefined) {
        _notifyI18n();
        return;
      }
      el.appendChild(saved);
      processTree(el);
      _notifyI18n();
    });
  },
});
